import React, { Component } from 'react';
import ValidationText from '../../utils/validation/ValidationText';
import { Navigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeftLong, faCircleNotch, faMinus, faPlus, faTrashCan, faUpload, faFileAlt } from '@fortawesome/free-solid-svg-icons';
import SwalServices from '../../services/swalServices/SwalServices';
import AdminServices from '../../services/axiosServices/apiServices/adminServices';
import { CommonSuccessMessages } from '../../utils/Messages';
import CreatableSelect from 'react-select/creatable';

// import MultiSelectDropdown from './MultiSelectDropdown'; // adjust path if needed
// import ValidationText from './ValidationText'; // adjust path if needed

class FormDetails extends Component {
    constructor(props) {
        super(props);
        this.swalServices = new SwalServices();
        this.adminServices = new AdminServices();
        this.state = {
            formId: 0,
            formName: '',
            formDescription: '',
            notificationEmail: '',
            sections: [],
            isAnonymous: 0,
            enableEmailForCandidate: 0,
            validState: {
                error: {}
            }
        };
    }

    handleChange = (event, field, sectionIndex = null, questionIndex = null) => {
        const value = event.target.value;
        if (sectionIndex === null) {
            // For formName and formDescription
            this.setState({ [field]: value });
        } else if (questionIndex === null) {
            // For Section fields
            const updatedSections = [...this.state.sections];
            updatedSections[sectionIndex][field] = value;
            this.setState({ sections: updatedSections });
        } else {
            // For Question fields
            const updatedSections = [...this.state.sections];
            updatedSections[sectionIndex].questions[questionIndex][field] = value;
            this.setState({ sections: updatedSections });
        }
    };

    addSection = () => {
        const { sections, formName } = this.state;

        // Check if formName is filled
        if (!formName.trim()) {
            this.swalServices.Error('Please fill the Form Name before adding a section.');
            return;
        }

        // If there are sections, check if the last section's name is filled
        if (sections.length > 0) {
            const lastSection = sections[sections.length - 1];
            if (!lastSection.sectionName.trim()) {
                this.swalServices.Error(`Please fill the Section ${sections.length} Name before adding a new section.`);
                return;
            }
        }

        // Add the new section if all conditions are met
        const newSection = {
            sectionName: '',
            secDescription: '',
            questions: []
        };

        this.setState(prevState => ({
            sections: [...prevState.sections, newSection]
        }));
    };

    removeSection = (index) => {
        const updatedSections = [...this.state.sections];
        updatedSections.splice(index, 1);
        this.setState({ sections: updatedSections });
    };

    addQuestion = (sectionIndex) => {
        const { sections } = this.state;
        const section = sections[sectionIndex];

        if (section.questions.length > 0) {
            const lastQuestion = section.questions[section.questions.length - 1];
            if (!lastQuestion.question.trim()) {
                this.swalServices.Error(`Please fill the previous question ${sectionIndex + 1} before adding a new question.`);
                return;
            }
        }

        const newQuestion = {
            question: '',
            questionType: '',
            isRequired: false,
            options: [],
            sortNumber: 1
        };
        const updatedSections = [...sections];
        updatedSections[sectionIndex].questions.push(newQuestion);
        this.setState({ sections: updatedSections });
    };

    removeQuestion = (sectionIndex, questionIndex) => {
        const updatedSections = [...this.state.sections];
        updatedSections[sectionIndex].questions.splice(questionIndex, 1);
        this.setState({ sections: updatedSections });
    };

    handleSelectChange = (selected, field, sectionIndex = null, questionIndex = null) => {
        if (sectionIndex === null) {
            this.setState({ [field]: selected });
        } else {
            const updatedSections = [...this.state.sections];
            updatedSections[sectionIndex].questions[questionIndex][field] = selected;
            this.setState({ sections: updatedSections });
        }
    };

    handleAddOption = (sectionIndex, questionIndex) => {
        const updatedSections = [...this.state.sections];
        const question = updatedSections[sectionIndex].questions[questionIndex];

        // Check if there are options and if the last one is empty
        if (question.options.length > 0) {
            const lastOption = question.options[question.options.length - 1];
            if (!lastOption.trim()) {
                this.swalServices.Error(`Please fill the previous option before adding a new option.`);
                return;
            }
        }

        // Now safe to add new option
        question.options.push('');
        this.setState({ sections: updatedSections });
    };

    removeOption = (sectionIndex, questionIndex, optionIndex) => {
        const updatedSections = [...this.state.sections];
        // Remove the option from the question's options array
        updatedSections[sectionIndex].questions[questionIndex].options.splice(optionIndex, 1);
        this.setState({ sections: updatedSections });
    };

    handleOptionChange = (e, sectionIndex, questionIndex, optionIndex) => {
        const updatedSections = [...this.state.sections];
        updatedSections[sectionIndex].questions[questionIndex].options[optionIndex] = e.target.value;
        this.setState({ sections: updatedSections });
    };

    createForm = () => {
        const { formName, formDescription, notificationEmail, isAnonymous, enableEmailForCandidate, sections } = this.state;

        // Validate that the form name is entered
        if (!formName.trim()) {
            this.swalServices.Error("Form name is required.");
            return;
        }

        // Validate email format if provided
        if (notificationEmail && notificationEmail.trim()) {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            const emails = notificationEmail.split(',').map(email => email.trim()).filter(email => email.length > 0);
            
            // Check if any email is invalid
            const invalidEmails = emails.filter(email => !emailRegex.test(email));
            if (invalidEmails.length > 0) {
                this.swalServices.Error(`Please enter valid email addresses. Invalid emails: ${invalidEmails.join(', ')}`);
                return;
            }
        }

        // Ensure there is at least one section with at least one question
        if (sections.length === 0 || !sections.some(section => section.questions.length > 0)) {
            this.swalServices.Error("Please add at least one section with one question.");
            return;
        }

        // Validate each section and its questions
        for (let section of sections) {
            if (!section.sectionName.trim()) {
                this.swalServices.Error(`Section name is required in section ${sections.indexOf(section) + 1}.`);
                return;
            }

            for (let question of section.questions) {
                if (!question.question.trim()) {
                    this.swalServices.Error(`Question is required in section ${section.sectionName} - Question ${section.questions.indexOf(question) + 1}.`);
                    return;
                }

                if (!question.questionType) {
                    this.swalServices.Error(`Question type is required for question in section ${section.sectionName} - Question ${section.questions.indexOf(question) + 1}.`);
                    return;
                }

                if (!question.sortNumber || question.sortNumber <= 0 || question.sortNumber > 200) {
                    this.swalServices.Error(`Please select Sort Number between 1 - 200 for question in section ${section.sectionName} - Question ${section.questions.indexOf(question) + 1}.`);
                    return;
                }

                // Check if the question type is checkbox, radio, or dropdown, and validate options
                if (['checkbox', 'radio', 'dropdown'].includes(question.questionType) && (!question.options || question.options.length === 0)) {
                    this.swalServices.Error(`At least one option is required for question ${section.sectionName} - Question ${section.questions.indexOf(question) + 1}.`);
                    return;
                }

                // Check if the question type is document and validate document is uploaded
                if (question.questionType === 'document' && !question.documentPath) {
                    this.swalServices.Error(`Please upload a document for question ${section.sectionName} - Question ${section.questions.indexOf(question) + 1}.`);
                    return;
                }
            }
        }

        // Construct payload after validation passes
        const payload = {
            isAnonymous,
            enableEmailForCandidate,
            formName,
            formDescription,
            notificationEmail: notificationEmail.trim() || '',
            questionDetails: sections.map(section => ({
                sectionName: section.sectionName,
                secDescription: section.secDescription,
                questionData: section.questions.map(question => {
                    const questionItem = {
                        question: question.question,
                        questionType: question.questionType,
                        isRequired: question.isRequired,
                        sortNumber: question.sortNumber,
                    };
                    // Add options only if question type supports it and options exist
                    if (['radio', 'checkbox', 'dropdown'].includes(question.questionType) && question.options?.length > 0) {
                        questionItem.options = question.options;
                    }
                    // Add documentPath for document share type
                    if (question.questionType === 'document' && question.documentPath) {
                        questionItem.documentPath = question.documentPath;
                        questionItem.documentName = question.documentName;
                    }
                    return questionItem;
                })
            }))
        };

        this.setState({ isLoading: true })
        this.adminServices.createForm(payload).then((response) => {
            if (response.success === 1) {
                this.setState({ route: "/form-list" })
                this.swalServices.Success(response.msg)
            } else {
                this.swalServices.Error(response.msg)
            }
            this.setState({ isLoading: false });
        });
    };

    updateForm = () => {
        const { formName, formDescription, notificationEmail, isAnonymous, enableEmailForCandidate, sections } = this.state;

        // Validate that the form name is entered
        if (!formName.trim()) {
            this.swalServices.Error("Form name is required.");
            return;
        }

        // Validate email format if provided
        if (notificationEmail && notificationEmail.trim()) {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            const emails = notificationEmail.split(',').map(email => email.trim()).filter(email => email.length > 0);
            
            // Check if any email is invalid
            const invalidEmails = emails.filter(email => !emailRegex.test(email));
            if (invalidEmails.length > 0) {
                this.swalServices.Error(`Please enter valid email addresses. Invalid emails: ${invalidEmails.join(', ')}`);
                return;
            }
        }

        // Ensure there is at least one section with at least one question
        if (sections.length === 0 || !sections.some(section => section.questions.length > 0)) {
            this.swalServices.Error("Please add at least one section with one question.");
            return;
        }

        // Validate each section and its questions
        for (let section of sections) {
            if (!section.sectionName.trim()) {
                this.swalServices.Error(`Section name is required in section ${sections.indexOf(section) + 1}.`);
                return;
            }

            for (let question of section.questions) {
                if (!question.question.trim()) {
                    this.swalServices.Error(`Question is required in section ${section.sectionName} - Question ${section.questions.indexOf(question) + 1}.`);
                    return;
                }

                if (!question.questionType) {
                    this.swalServices.Error(`Question type is required for question in section ${section.sectionName} - Question ${section.questions.indexOf(question) + 1}.`);
                    return;
                }

                if (!question.sortNumber || question.sortNumber <= 0 || question.sortNumber > 200) {
                    this.swalServices.Error(`Please select Sort Number between 1 - 200 for question in section ${section.sectionName} - Question ${section.questions.indexOf(question) + 1}.`);
                    return;
                }

                // Check if the question type is checkbox, radio, or dropdown, and validate options
                if (['checkbox', 'radio', 'dropdown'].includes(question.questionType) && (!question.options || question.options.length === 0)) {
                    this.swalServices.Error(`At least one option is required for question ${section.sectionName} - Question ${section.questions.indexOf(question) + 1}.`);
                    return;
                }

                // Check if the question type is document and validate document is uploaded
                if (question.questionType === 'document' && !question.documentPath) {
                    this.swalServices.Error(`Please upload a document for question ${section.sectionName} - Question ${section.questions.indexOf(question) + 1}.`);
                    return;
                }
            }
        }

        // Construct payload after validation passes
        const payload = {
            isAnonymous,
            enableEmailForCandidate,
            formName,
            formDescription,
            notificationEmail: notificationEmail.trim() ? notificationEmail.split(',').map(email => email.trim()).filter(email => email.length > 0).join(',') : "",
            questionDetails: sections.map(section => ({
                sectionName: section.sectionName,
                secDescription: section.secDescription,
                questionData: section.questions.map(question => {
                    const questionItem = {
                        question: question.question,
                        questionType: question.questionType,
                        isRequired: question.isRequired,
                        sortNumber: question.sortNumber,
                    };
                    // Add options only if question type supports it and options exist
                    if (['radio', 'checkbox', 'dropdown'].includes(question.questionType) && question.options?.length > 0) {
                        questionItem.options = question.options;
                    }
                    // Add documentPath for document share type
                    if (question.questionType === 'document' && question.documentPath) {
                        questionItem.documentPath = question.documentPath;
                        questionItem.documentName = question.documentName;
                    }
                    return questionItem;
                })
            }))
        };

        this.setState({ isLoading: true })
        this.adminServices.updateForm(payload, this.state.formId).then((response) => {
            if (response.success === 1) {
                this.setState({ route: "/form-list" })
                this.swalServices.Success(response.msg)
            } else {
                this.swalServices.Error(response.msg)
            }
            this.setState({ isLoading: false });
        });
    };

    handleDelete = () => {
        this.swalServices.Confirm(
            "",
            ``,
            CommonSuccessMessages.Yes_Text,
            CommonSuccessMessages.No_Text,
            `Are you sure you want to delete ?`
        ).then((response) => {
            if (response === true) {
                this.setState({ isLoading: true });
                let id = this.state.formId
                this.adminServices.deleteForm(id).then((response) => {
                    if (response.success === 1) {
                        this.swalServices.Success(response.msg);
                        setTimeout(() => {
                            this.setState({ route: "/form-list" })
                        }, 1000);
                    } else {
                        this.swalServices.Error(response.msg);
                        this.setState({ isLoading: false });
                    }
                });
            }
        });
    }

    componentDidMount() {
        let params = new URLSearchParams(window.location.search);
        let formId = Number(params.get(""))
        if (formId) {
            if (formId != null && formId > 0) {
                this.setState({ formId: formId }, () => {
                    this.getFormData(this.state.formId)
                });
            }
        }
    }

    getFormData = (id) => {
        this.adminServices.getFormData(id).then((response) => {
            if (response.success === 1 && response.data !== null) {
                const { formName, formDescription, notificationEmail, isAnonymous, enableEmailForCandidate, questionDetails } = response.data;

                // Transforming the response data for sections and questions
                const sections = questionDetails.map(section => ({
                    sectionName: section.sectionName,
                    secDescription: section.secDescription,
                    questions: section?.questionData?.map(q => ({
                        question: q.question,
                        questionType: q.questionType,
                        isRequired: q.isRequired === 1 ? true : false,
                        sortNumber: q.sortNumber,
                        options: q.options || [],
                        documentPath: q.documentPath || '',
                        documentName: q.documentName || ''
                    }))
                }));

                // Set the form data in state
                this.setState({
                    formName: formName,
                    formDescription: formDescription,
                    notificationEmail: notificationEmail || '',
                    isAnonymous: isAnonymous,
                    enableEmailForCandidate: enableEmailForCandidate || 0,
                    sections: sections
                });

            } else {
                this.swalServices.Error(response.msg);
            }
        }).catch((error) => {
            console.error("Error fetching form data:", error);
            this.swalServices.Error("An error occurred while fetching form data.");
        });
    }

    handleCreatableOptionsChange = (newValue, sectionIndex, questionIndex) => {
        const updatedSections = [...this.state.sections];
        const question = updatedSections[sectionIndex].questions[questionIndex];
        question.options = newValue.map((opt) => opt.value); // Convert to array of strings
        this.setState({ sections: updatedSections });
    };

    handleDocumentUpload = async (e, sectionIndex, questionIndex) => {
        const file = e.target.files[0];
        if (!file) return;

        // Validate file type (PDF, DOC, DOCX)
        const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
        if (!allowedTypes.includes(file.type)) {
            this.swalServices.Error('Please upload a PDF or Word document only');
            return;
        }

        // Validate file size (max 10MB)
        if (file.size > 10 * 1024 * 1024) {
            this.swalServices.Error('File size should not exceed 10MB');
            return;
        }

        const formData = new FormData();
        formData.append('document', file);

        try {
            const response = await this.adminServices.uploadFormDocument(formData);
            if (response.success === 1) {
                const updatedSections = [...this.state.sections];
                updatedSections[sectionIndex].questions[questionIndex].documentPath = response.data.filePath;
                updatedSections[sectionIndex].questions[questionIndex].documentName = file.name;
                this.setState({ sections: updatedSections });
                this.swalServices.Success('Document uploaded successfully');
            } else {
                this.swalServices.Error(response.msg || 'Failed to upload document');
            }
        } catch (error) {
            console.error('Upload error:', error);
            this.swalServices.Error('Failed to upload document. Please try again.');
        }
    };

    removeDocument = (sectionIndex, questionIndex) => {
        const updatedSections = [...this.state.sections];
        updatedSections[sectionIndex].questions[questionIndex].documentPath = '';
        updatedSections[sectionIndex].questions[questionIndex].documentName = '';
        this.setState({ sections: updatedSections });
    };


    render() {
        if (this.state.route != null) {
            return <Navigate to={this.state.route} />;
        }
        const { formName, formDescription, sections, validState } = this.state;

        return (
            <div className='form-details'>
                <div className='form-details-inner p-[30px] overflow-y-auto' style={{ height: "calc(100vh - 168.8px)" }}>
                    <div className='title flex items-center justify-between mb-[10px]'>
                        <h1 className='text-[22px] m-0 font-semibold text-[#004AAD]' style={{ lineHeight: "30px" }}>
                            {this.state.formId > 0 ? 'Edit form' : 'Create form'}
                        </h1>
                        {/* <button className='flex items-center gap-[5px] py-[5px] px-[10px] text-[14px] font-medium rounded-[5px]'><FontAwesomeIcon className='w-5 h-5' icon={faUserPlus} />Add user</button> */}
                    </div>
                    <div className='form-data grid gap-[30px]'>
                        <div className='form-info p-5 bg-white rounded-[16px]' style={{ border: "1px solid #DFDFDF" }}>
                            <div className='flex items-center justify-between mb-5'>
                                <h2 className='text-[16px] font-semibold mt-0'>Form details</h2>
                                <div className='flex items-center gap-5'>
                                    <label className='text-[14px] mt-0 flex flex-row-reverse items-center gap-2' title={this.state.isAnonymous === 1 ? 'Cannot send copy to candidate for anonymous forms' : 'Send a copy of submission to candidate email'}>
                                        Send copy to candidate
                                        <input
                                            type='checkbox'
                                            className='w-5 h-5 cursor-pointer'
                                            checked={this.state.enableEmailForCandidate === 1}
                                            disabled={this.state.isAnonymous === 1}
                                            onChange={(e) => this.setState({ enableEmailForCandidate: e.target.checked ? 1 : 0 })}
                                        />
                                    </label>
                                    <label className='text-[14px] mt-0 flex flex-row-reverse items-center gap-2'>
                                        Anonymous
                                        <input
                                            type='checkbox'
                                            className='w-5 h-5 cursor-pointer'
                                            checked={this.state.isAnonymous === 1}
                                            onChange={(e) => {
                                                const isAnonymous = e.target.checked ? 1 : 0;
                                                this.setState({
                                                    isAnonymous,
                                                    enableEmailForCandidate: isAnonymous === 1 ? 0 : this.state.enableEmailForCandidate
                                                });
                                            }}
                                        />
                                    </label>
                                </div>
                            </div>

                            <div className="dynamic-form-builder">
                                <div className="input-grp  col-span-2"><label>Form Name<span className='req'>*</span></label>
                                    <input
                                        type="text"
                                        placeholder="Enter form name"
                                        className={`${validState?.error?.formName ? 'error' : ''}`}
                                        value={formName}
                                        onChange={(e) => this.handleChange(e, "formName")}
                                    />
                                    <ValidationText error={validState.error.formName} />
                                </div>

                                <div className="input-grp  col-span-2">
                                    <label>Form Description:</label>
                                    <textarea
                                        rows="3"
                                        placeholder="Enter form description"
                                        className={`${validState?.error?.formDescription ? 'error' : ''}`}
                                        value={formDescription}
                                        onChange={(e) => this.handleChange(e, "formDescription")}
                                        style={{ resize: 'vertical', minHeight: '80px' }}
                                    />
                                    <ValidationText error={validState.error.formDescription} />
                                </div>

                                <div className="input-grp  col-span-2">
                                    <label>Notification Email (Optional):</label>
                                    <input
                                        type="email"
                                        placeholder="Enter email addresses (comma-separated) for form submission notifications. Default will be the 'compliance@arbourhealthcare.co.uk'"
                                        className={`${validState?.error?.notificationEmail ? 'error' : ''}`}
                                        value={this.state.notificationEmail}
                                        onChange={(e) => this.handleChange(e, "notificationEmail")}
                                    />
                                    <ValidationText error={validState.error.notificationEmail} />
                                </div>

                                {/* <div className='btn-left mt-5'>
                                    <button
                                        onClick={() => { this.addSection({ route: "/jobs" }) }}
                                        className='font-medium text-[12px] text-[#fff] leading-[24px] px-[15px] py-[8px] rounded-[14px] bg-green-500 hover:bg-green-600 border-0 flex items-center gap-[10px]'
                                    >
                                        <FontAwesomeIcon icon={faPlus} className='w-3 h-3' />
                                        Section
                                    </button>

                                </div> */}

                                {sections.map((section, sectionIndex) => (
                                    <div key={sectionIndex} className="section-block">
                                        <hr />

                                        <div className="section-header">
                                            {/* <h3 className='text-[14px] font-semibold mt-0'>Section {sectionIndex + 1}</h3> */}
                                            <div className='btn-left mt-5 mb-5'>
                                                <button
                                                    onClick={() => { this.removeSection(sectionIndex) }}
                                                    className='font-medium text-[12px] text-[#ffffff] leading-[24px] px-[15px] py-[8px] rounded-[14px] bg-[#EA3636] hover:bg-[#c53030] border-0 flex items-center gap-[10px]'>
                                                    <FontAwesomeIcon icon={faMinus} className='w-3 h-3' />Section {sectionIndex + 1}
                                                </button>
                                            </div>

                                        </div>

                                        <div className="input-grp  col-span-2">
                                            <label>Section Name <span className='req'>*</span></label>
                                            <input
                                                type="text"
                                                placeholder="Enter section name"
                                                value={section.sectionName}
                                                onChange={(e) => this.handleChange(e, "sectionName", sectionIndex)}
                                            />
                                        </div>

                                        <div className="input-grp  col-span-2">
                                            <label>Section Description:</label>
                                            <textarea
                                                rows="2"
                                                placeholder="Enter section description"
                                                value={section.secDescription}
                                                onChange={(e) => this.handleChange(e, "secDescription", sectionIndex)}
                                                style={{ resize: 'vertical', minHeight: '60px' }}
                                            />
                                        </div>

                                        {section.questions.map((question, questionIndex) => (
                                            <div key={questionIndex} className="question-block">
                                                {/* <h4>Question {questionIndex + 1}</h4> */}
                                                <div className='btn-left mt-5 mb-5'>
                                                    <button
                                                        onClick={() => { this.removeQuestion(sectionIndex, questionIndex) }}
                                                        className='font-medium text-[12px] text-[#ffffff] leading-[24px] px-[15px] py-[8px] rounded-[14px] bg-[#EA3636] hover:bg-[#c53030] border-0 flex items-center gap-[10px]'>
                                                        <FontAwesomeIcon icon={faMinus} className='w-3 h-3' />Question {questionIndex + 1}
                                                    </button>
                                                </div>
                                                {/* <button type="button" onClick={() => this.removeQuestion(sectionIndex, questionIndex)}>🗑️ Remove Question</button> */}

                                                <div className="input-grp ">
                                                    <label>Question Text<span className='req'>*</span></label>
                                                    <input
                                                        type="text"
                                                        placeholder="Enter question"
                                                        value={question.question}
                                                        onChange={(e) => this.handleChange(e, "question", sectionIndex, questionIndex)}
                                                    />
                                                </div>

                                                <div className='flex relative inline-flex' style={{ justifyContent: 'space-between', width: "60%" }}>
                                                    <div className="input-grp ">
                                                        <label>Question Type<span className='req'>*</span></label>
                                                        <select
                                                            value={question.questionType}
                                                            onChange={(e) => this.handleChange(e, "questionType", sectionIndex, questionIndex)}
                                                        >
                                                            <option value="">Select Type</option>
                                                            <option value="text">Text</option>
                                                            <option value="textarea">Textarea</option>
                                                            {/* <option value="texteditor">Text Editor</option> */}
                                                            <option value="email">Email</option>
                                                            <option value="date">Date</option>
                                                            <option value="time">Time</option>
                                                            <option value="radio">Radio</option>
                                                            <option value="checkbox">Checkbox</option>
                                                            <option value="dropdown">Dropdown</option>
                                                            <option value="signature">Signature</option>
                                                            <option value="document">Document Share</option>
                                                        </select>
                                                    </div>

                                                    <div className="input-grp mt-5">
                                                        <label>Is Required:</label>
                                                        <label className="switch">
                                                            <input
                                                                type="checkbox"
                                                                checked={question.isRequired}
                                                                onChange={(e) => {
                                                                    const updatedSections = [...this.state.sections];
                                                                    updatedSections[sectionIndex].questions[questionIndex].isRequired = e.target.checked;
                                                                    this.setState({ sections: updatedSections });
                                                                }}
                                                            />
                                                            <span className="slider round"></span>
                                                        </label>
                                                    </div>

                                                    <div className="input-grp ">
                                                        <label>Sort Number:</label>
                                                        <input
                                                            type="number"
                                                            placeholder="Enter sort number"
                                                            value={question.sortNumber}
                                                            onChange={(e) => this.handleChange(e, "sortNumber", sectionIndex, questionIndex)}
                                                        />
                                                    </div>
                                                </div>

                                                {/* {(question.questionType === 'radio' ||
                                                    question.questionType === 'checkbox' ||
                                                    question.questionType === 'dropdown') && (
                                                        <div className="options-block">
                                                            <h5>Options<span className='req'>*</span></h5>
                                                            {question.options.map((option, optionIndex) => (
                                                                <div key={optionIndex} className="input-grp flex gap-2.5">
                                                                    <input
                                                                        type="text"
                                                                        placeholder="Enter option"
                                                                        value={option}
                                                                        onChange={(e) => this.handleOptionChange(e, sectionIndex, questionIndex, optionIndex)}
                                                                    />
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => this.removeOption(sectionIndex, questionIndex, optionIndex)}
                                                                        className="text-[#EA3636] hover:text-[#ffffff] rounded-[8px] border-0 bg-[#ffe8e8] hover:bg-[#EA3636] p-2.5 w-fit h-fit"
                                                                    >
                                                                        <FontAwesomeIcon icon={faMinus} className="w-3 h-3" />
                                                                    </button>
                                                                </div>
                                                            ))}
                                                            <div className='btn-left mt-5'>
                                                                <button
                                                                    onClick={() => { this.handleAddOption(sectionIndex, questionIndex) }}
                                                                    className='font-medium text-[12px] text-green-500 leading-[24px] px-[15px] py-[8px] rounded-[14px] bg-transparent border-2 border-green-500 hover:bg-green-100 flex items-center gap-[10px]'
                                                                >
                                                                    <FontAwesomeIcon icon={faPlus} className='w-3 h-3' />
                                                                    Option
                                                                </button>
                                                            </div>
                                                        </div>
                                                    )} */}

                                                {(question.questionType === 'radio' ||
                                                    question.questionType === 'checkbox' ||
                                                    question.questionType === 'dropdown') && (
                                                        <div className="input-grp ">
                                                            <label>Options<span className="req">*</span></label>
                                                            <div className="options-block">
                                                                {/* <h5> */}
                                                                {/* Options<span className="req">*</span> */}
                                                                {/* </h5> */}

                                                                <CreatableSelect
                                                                    isMulti
                                                                    placeholder="Type and press Enter to add option"
                                                                    value={question.options.map((opt) => ({ label: opt, value: opt }))}
                                                                    onChange={(newValue) =>
                                                                        this.handleCreatableOptionsChange(newValue, sectionIndex, questionIndex)
                                                                    }
                                                                    className="react-select-container"
                                                                    classNamePrefix="react-select"
                                                                />
                                                            </div>
                                                        </div>
                                                    )}

                                                {/* Document Upload for Document Share type */}
                                                {question.questionType === 'document' && (
                                                    <div className="input-grp">
                                                        <label>Upload Document<span className="req">*</span></label>
                                                        <p className="text-[12px] text-gray-500 mb-2">Upload a PDF or Word document that will be shared with users who submit this form</p>
                                                        {question.documentPath ? (
                                                            <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg border border-green-200">
                                                                <FontAwesomeIcon icon={faFileAlt} className="w-5 h-5 text-green-600" />
                                                                <span className="flex-1 text-[14px] text-green-800 truncate">{question.documentName || 'Document uploaded'}</span>
                                                                <button
                                                                    type="button"
                                                                    onClick={() => this.removeDocument(sectionIndex, questionIndex)}
                                                                    className="text-[#EA3636] hover:text-[#ffffff] rounded-[8px] border-0 bg-[#ffe8e8] hover:bg-[#EA3636] p-2 w-fit h-fit"
                                                                >
                                                                    <FontAwesomeIcon icon={faTrashCan} className="w-3 h-3" />
                                                                </button>
                                                            </div>
                                                        ) : (
                                                            <div className="relative">
                                                                <input
                                                                    type="file"
                                                                    accept=".pdf,.doc,.docx"
                                                                    onChange={(e) => this.handleDocumentUpload(e, sectionIndex, questionIndex)}
                                                                    className="hidden"
                                                                    id={`document-upload-${sectionIndex}-${questionIndex}`}
                                                                />
                                                                <label
                                                                    htmlFor={`document-upload-${sectionIndex}-${questionIndex}`}
                                                                    className="flex items-center gap-2 px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-[#0077B6] hover:bg-[#f0f9ff] transition-all"
                                                                >
                                                                    <FontAwesomeIcon icon={faUpload} className="w-5 h-5 text-gray-400" />
                                                                    <span className="text-[14px] text-gray-500">Click to upload document (PDF, DOC, DOCX - Max 10MB)</span>
                                                                </label>
                                                            </div>
                                                        )}
                                                    </div>
                                                )}


                                            </div>
                                        ))}
                                        <div className='btn-left mt-5'>
                                            <button
                                                onClick={() => { this.addQuestion(sectionIndex) }}
                                                className='font-medium text-[12px] text-[#fff] leading-[24px] px-[15px] py-[8px] rounded-[14px] bg-green-500 hover:bg-green-600 border-0 flex items-center gap-[10px]'>
                                                <FontAwesomeIcon icon={faPlus} className='w-3 h-3' />Question
                                            </button>
                                        </div>
                                        {/* <button type="button" onClick={() => this.addQuestion(sectionIndex)}>➕ Add Question</button> */}



                                    </div>
                                ))}
                                <div className='btn-left mt-5'>
                                    <button
                                        onClick={() => { this.addSection({ route: "/jobs" }) }}
                                        className='font-medium text-[12px] text-[#fff] leading-[24px] px-[15px] py-[8px] rounded-[14px] bg-[#0077B6] hover:bg-[#3b82f6] border-0 flex items-center gap-[10px]'
                                    >
                                        <FontAwesomeIcon icon={faPlus} className='w-3 h-3' />
                                        Section
                                    </button>

                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className='sticky bottom-0 bg-white border-t border-slate-200 z-30 px-[30px] py-[30px] flex items-center justify-between' style={{ borderTop: "1px solid rgb(0,0,0,0.1)", boxShadow: "0 -7px 12px -6px rgb(14, 63, 126, 0.04)" }}>
                    <div className='btn-left'>
                        <button
                            onClick={() => { this.setState({ route: "/form-list" }) }}
                            className='font-medium text-[16px] text-[#223645] leading-[24px] px-[24px] py-[12px] rounded-[14px] bg-[#EBEBEB] hover:bg-[#dddddd] border-0 flex items-center gap-[5px]'>
                            <FontAwesomeIcon icon={faArrowLeftLong} className='w-5 h-5' />Back
                        </button>
                    </div>
                    <div className='btn-right flex items-center gap-5'>
                        {this.state.formId > 0 ?
                            <>
                                <button
                                    onClick={() => this.handleDelete()}
                                    className='font-medium text-[16px] text-[#EA3636] hover:text-[#ffffff] leading-[24px] px-[24px] py-[12px] rounded-[14px] border-0 bg-[#ffe8e8] hover:bg-[#EA3636] flex items-center gap-[5px]'>
                                    <FontAwesomeIcon icon={faTrashCan} className='w-5 h-5' />Delete
                                </button>
                                <button
                                    onClick={() => this.updateForm()}
                                    className='font-medium text-[16px] text-[#FFFFFF] leading-[24px] px-[24px] py-[12px] rounded-[14px] bg-[#0077B6] hover:bg-[#3b82f6] border-0'>
                                    {this.state.isLoading ? <FontAwesomeIcon icon={faCircleNotch} spin /> : "Update"}
                                </button>
                            </>
                            :
                            <button
                                onClick={() => this.createForm()}
                                className='font-medium text-[16px] text-[#FFFFFF] leading-[24px] px-[24px] py-[12px] rounded-[14px] bg-[#0077B6] hover:bg-[#3b82f6] border-0'>
                                {this.state.isLoading ? <FontAwesomeIcon icon={faCircleNotch} spin /> : "Save"}
                            </button>
                        }
                    </div>
                </div>
            </div >

        );
    }
}

export default FormDetails;
